// Source of truth for the app's copy. The other locales are typed
// against this object, so a missing or extra key fails the build.
export const en = {
  accountSelect: {
    empty: "No accounts connected.",
    placeholder: "Select accounts",
    selected_one: "{{count}} account selected",
    selected_other: "{{count}} accounts selected",
  },
  activity: {
    allSelected: "All activities selected",
    description: "View your organization's activity logs",
    details: {
      createdAt: "Created At",
      description: "View detailed information about this activity log entry.",
      noNote: "No note provided",
      title: "Activity Details",
      updatedAt: "Updated At",
    },
    empty: "No activities logged yet.",
    search: "Search activity logs...",
    selectAll: "Select all activities",
    title: "Activity Logs",
    undoSelectAll: "Undo select all activities",
  },
  auth: {
    backToLogin: "Back to login",
    connectingShopify: "Connecting to Shopify...",
    connectionFailed: "Connection Failed",
    ebay: {
      connecting: "Connecting your eBay account...",
      connectionFailedWith: "eBay connection failed: {{error}}",
      noCode: "No authorization code received from eBay",
      pleaseWait: "Please wait while we complete the connection process.",
      redirectWhenFinished: "You will be redirected back to the application when finished.",
      redirecting: "Redirecting you back to the application...",
      successBody: "Your eBay account has been connected successfully.",
      successTitle: "Successfully Connected!",
      tryAgain: "Please try connecting your eBay account again.",
    },
    email: "Email",
    emailPlaceholder: "Enter your email address...",
    login: {
      continueWithShopify: "Continue with Shopify",
      createAccount: "Create an account",
      invalidCredentials: "Invalid email or password",
      newHere: "New here?",
      title: "Log in to Inventoree",
    },
    or: "OR",
    returnToShopifyFailed: "Failed to return to Shopify",
    shopifyConnect: {
      loggingIn: "Logging in with Shopify...",
      loginFailed: "Failed to log in with Shopify",
      missingParams: "Missing Shopify authorization parameters. Please try again.",
    },
    shopifyLogin: {
      billingShop: "This will be the shop that handles billing",
      enterValidDomain: "Enter a valid shop domain, e.g. my-store.myshopify.com",
      invalidDomain: "Invalid shopify domain",
      loginWithEmail: "Log in with email",
      shopDomain: "Shop domain",
      subtitle: "Enter your shop's domain to continue.",
      title: "Log in with Shopify",
      useEmail: "Use email instead?",
    },
    shopifyLogoAlt: "Shopify logo",
    signup: {
      haveAccount: "Already have an account?",
      login: "Log in",
      title: "Sign up to Inventoree",
    },
    tiktok: {
      connecting: "Connecting to TikTok...",
      noCode: "TikTok didn't return an authorization code.",
      tryAgain: "Please try connecting your TikTok account again.",
    },
    verify: {
      invalidPin: "Pin is invalid, please try again",
      newCodeSent: "New code sent",
      noCode: "Didn't receive the code?",
      resend: "Resend",
      resendIn: "Resend ({{seconds}}s)",
      submit: "Verify Code",
      subtitle: "Check your email for the code we sent you",
      title: "Let's verify your email",
      verifying: "Verifying...",
    },
  },
  collections: {
    back: "Collections",
    columns: {
      conditions: "Product conditions",
      products: "Products",
      title: "Title",
    },
    create: "Create collection",
    delete: {
      body: "This deletes the collection. Its products stay in your inventory.",
      bodyMany: "This deletes the collections. Their products stay in your inventory.",
      button: "Delete collection",
      title: "Delete {{title}}?",
      titleMany_one: "Delete {{count}} collection?",
      titleMany_other: "Delete {{count}} collections?",
    },
    description: "Group products into collections by hand, or with conditions that add matching products automatically.",
    empty: "No collections yet",
    emptyBody: "Create a collection to group products together, like a sale or a season.",
    form: {
      addCondition: "Add another condition",
      applyMetafields: "Apply",
      clearMetafield: "Stop filling {{aspect}} from a metafield",
      conditionInvalid: "Every condition needs a value. Prices and stock need a number.",
      conditionPlaceholder: "Value",
      conditions: "Conditions",
      description: "Description",
      ebayCategories: "eBay categories",
      ebayCategoriesHint: "Optional. The category and item specifics this collection's products are listed with on each eBay marketplace. Products with a category of their own keep it, and a product in several collections takes the category of the oldest one. Without one, eBay suggests a category.",
      ebayConditionMetafieldHint: "The metafield's value has to be the name of one of the conditions eBay offers for the category, or its condition ID like 1000. Products with any other value aren't imported.",
      itemSpecificsDescription: "Products in this collection listed in {{category}} get these item specifics where they don't set their own. Empty ones are filled in from each product where it names a value.",
      manual: "Manual",
      manualHint: "Add products to this collection one by one.",
      mapMetafields: "Map metafields",
      mapMetafieldsDescription: "Choose the Shopify product vendor or metafield each item specific of {{category}} is filled from when products are imported. A product's value comes before the value set on the collection, and item specifics a product sets or maps on the import page come before both.",
      mapMetafieldsTitle: "Map metafields to item specifics",
      mappedFieldHint: "Filled from each product's “{{field}}” field in Shopify. This value is used for products where it's empty.",
      mappedSpecificHint: "Filled from each product's {{metafield}} metafield. This value is used for products where it's empty.",
      match: "Products must match",
      matchAll: "all conditions",
      matchAny: "any condition",
      metafieldDefinitionsFailed: "Couldn't load the metafield definitions from Shopify",
      metafieldsGroup: "Metafields",
      metafieldsMapped_one: "{{count}} item specific mapped",
      metafieldsMapped_other: "{{count}} item specifics mapped",
      noMetafieldDefinitions: "Your Shopify stores have no product metafield definitions that can fill item specifics. Add one for products in Shopify to map it here.",
      notMapped: "Not mapped",
      productFieldVendor: "Vendor",
      productFieldsGroup: "Shopify product",
      removeCondition: "Remove condition",
      removeEbayCategory: "Remove eBay category",
      removeEbayCategoryFor: "Remove the eBay category for {{name}}",
      saving: "Saving...",
      searchMetafields: "Search metafields",
      searchSpecifics: "Search item specifics",
      selectStore: "Select a store",
      smart: "Smart",
      smartHint: "Existing and future products that match the conditions are added automatically.",
      sortOrder: "Sort products",
      title: "Title",
      titlePlaceholder: "e.g. Summer collection, Under $100, Staff picks",
      titleRequired: "Title is required",
      type: "Collection type",
      typeLocked: "A collection's type can't be changed after it's created.",
    },
    import: {
      button: "Import from Shopify",
      confirm: "Import collections",
      description: "Every collection in the store is imported with the products it has in Shopify. Collections you imported before are updated.",
      done: "Imported {{created}} new and updated {{updated}} collections",
      from: "Collections will be imported from {{store}}.",
      importing: "Importing...",
      inProgress: "Still importing your collections. We'll notify you when it's done.",
      skipped_one: "{{count}} product isn't in Inventoree yet. Fetch your Shopify products, then resync to add it.",
      skipped_other: "{{count}} products aren't in Inventoree yet. Fetch your Shopify products, then resync to add them.",
      skippedHint: "Products that aren't in Inventoree yet are left out, fetch your Shopify products first to include them.",
      store: "Store",
      title: "Import collections from Shopify",
    },
    importedFrom: "From {{store}}",
    matchAll: "Matches all conditions",
    matchAny: "Matches any condition",
    noResults: "No collections match your search.",
    notFound: "This collection doesn't exist or was deleted.",
    products: {
      add: "Add products",
      added: "Added",
      addSelected_one: "Add {{count}} product",
      addSelected_other: "Add {{count}} products",
      browseDescription: "Search your inventory and pick the products to add.",
      empty: "There are no products in this collection.",
      emptyManual: "Add products to show them here.",
      emptySmart: "No products match the conditions yet.",
      moveDown: "Move down",
      moveUp: "Move up",
      noProducts: "No products found.",
      noSku: "No SKU",
      pickHint: "Pick the products this collection starts with. You can add more later.",
      price: "Price",
      remove: "Remove from collection",
      search: "Search products",
      smartHint: "Products are added and removed automatically by the collection's conditions.",
      sortHint: "Sort products manually to change their order.",
      title: "Products",
    },
    productsCount_one: "{{count}} product",
    productsCount_other: "{{count}} products",
    relations: {
      CONTAINS: "contains",
      ENDS_WITH: "ends with",
      EQUALS: "is equal to",
      GREATER_THAN: "is greater than",
      IS_NOT_SET: "is false",
      IS_SET: "is true",
      LESS_THAN: "is less than",
      NOT_CONTAINS: "does not contain",
      NOT_EQUALS: "is not equal to",
      STARTS_WITH: "starts with",
    },
    ruleColumns: {
      IS_PRICE_REDUCED: "Price reduced",
      PLATFORM_CONNECTION: "Store",
      TITLE: "Product title",
      VARIANT_COMPARE_AT_PRICE: "Compare-at price",
      VARIANT_INVENTORY: "Inventory stock",
      VARIANT_PRICE: "Price",
      VARIANT_SKU: "Variant SKU",
      VARIANT_TITLE: "Variant title",
    },
    search: "Search collections...",
    sort: {
      ALPHA_ASC: "Product title A-Z",
      ALPHA_DESC: "Product title Z-A",
      CREATED_ASC: "Oldest",
      CREATED_DESC: "Newest",
      MANUAL: "Manually",
      PRICE_ASC: "Lowest price",
      PRICE_DESC: "Highest price",
    },
    sync: {
      body: "This replaces the collection's title, description, sort order and products with the ones it has in Shopify. Changes you made here are lost.",
      button: "Resync",
      disconnected: "Imported from a store that isn't connected anymore",
      done: "Collection synced with Shopify",
      inProgress: "Still syncing the collection. We'll notify you when it's done.",
      lastSynced: "Last synced {{date}}",
      source: "Imported from {{store}}",
      syncing: "Syncing...",
      title: "Resync with Shopify?",
    },
    title: "Collections",
    toast: {
      created: "Collection created",
      deleted: "Collection deleted",
      deletedMany_one: "Deleted {{count}} collection",
      deletedMany_other: "Deleted {{count}} collections",
      failed: "Something went wrong. Please try again.",
      productsAdded_one: "Added {{count}} product",
      productsAdded_other: "Added {{count}} products",
      productsRemoved_one: "Removed {{count}} product",
      productsRemoved_other: "Removed {{count}} products",
      updated: "Collection saved",
    },
  },
  common: {
    apply: "Apply",
    cancel: "Cancel",
    clickIfNotRedirecting: "Click here if it's not redirecting",
    close: "Close",
    continue: "Continue",
    delete: "Delete",
    deleting: "Deleting...",
    disable: "Disable",
    disabled: "Disabled",
    enable: "Enable",
    enabled: "Enabled",
    generating: "Generating...",
    internalError: "Internal error",
    loading: "Loading",
    networkError: "Network error occurred",
    noResults: "No results.",
    notAvailable: "N/A",
    notSet: "Not set",
    openMenu: "Open menu",
    redirectingSoon: "Redirecting soon,",
    retry: "Retry",
    save: "Save",
    searchMinLength: "Enter at least {{min}} characters to search",
    sending: "Sending...",
    starting: "Starting...",
    updating: "Updating...",
  },
  connect: {
    button: {
      authorizing: "Authorizing...",
      completing: "Completing...",
      connect: "Connect",
      connecting: "Connecting...",
      failed: "Failed",
      redirecting: "Redirecting...",
      success: "Connected!",
    },
    ebayDescription: "Connect to eBay and start syncing your products.",
    ebayStartFailed: "Unable to start eBay connection",
    shopifyDescription: "Connect another Shopify store and start syncing your products.",
    status: {
      authorizing: "Waiting for authorization...",
      completing: "Completing connection...",
      failed: "Connection failed",
      redirectingToEbay: "Redirecting to eBay...",
      success: "Successfully connected!",
    },
    tiktokDescription: "Connect to TikTok Shop and start syncing your products.",
    tiktokStartFailed: "Unable to start TikTok connection",
  },
  dashboard: {
    analytics: {
      costValue: "Cost Value",
      inventoryValue: "Inventory Value",
      loadFailed: "Failed to load analytics data. Please try again later.",
      noStoreData: "No store data available",
      other: "Other",
      potentialRevenue: "Potential Revenue",
      productCount_one: "{{formatted}} product",
      productCount_other: "{{formatted}} products",
      products: "Products",
      storeBreakdown: "Store Breakdown",
      storeCount_one: "{{count}} store",
      storeCount_other: "{{count}} stores",
      title: "Analytics Overview",
      totalQuantity: "Total Quantity",
      unconnected: "Unconnected",
      unconnectedProducts: "Unconnected Products",
      unitCount_one: "{{formatted}} unit",
      unitCount_other: "{{formatted}} units",
      variants: "Variants",
    },
    exportBeta: {
      description: "Shopify to eBay product export support is now in Beta, you can go to the Shopify account import page to get started. If you see any issues please contact us immediately.",
      title: "New Shopify Export available in Beta",
    },
    toast: {
      ebayConnected: "eBay account connected! Fetching your products...",
      shopifyConnected: "Shopify account connected! Fetching your products...",
      subscribed: "Thank you for subscribing!",
      tiktokConnected: "TikTok account connected!",
    },
  },
  ebay: {
    fetch: {
      confirmTitle: "Confirm Fetch",
      estimate: "Inventoree found about {{listingCount}} active eBay listings across {{pageCount}} API pages. This will run in the background and may take a while for large accounts. Continue?",
      estimateFailed: "Could not estimate this account first. Start the background fetch anyway?",
      estimateFailedToast: "Could not estimate eBay listings. You can still start the fetch.",
      estimating: "Estimating...",
      noEstimate: "Inventoree could not estimate the listing count. The fetch will still run in the background. Continue?",
    },
    policies: {
      description: "Choose the eBay policies to use for future listings on this account.",
      empty: "No eBay business policies were found for this marketplace.",
      fulfillment: "Fulfillment policy",
      fulfillmentPlaceholder: "Select fulfillment policy",
      loadFailed: "Failed to load eBay policies.",
      payment: "Payment policy",
      paymentPlaceholder: "Select payment policy",
      return: "Return policy",
      returnPlaceholder: "Select return policy",
      title: "Business policies",
      validation: {
        fulfillment: "Select a fulfillment policy",
        payment: "Select a payment policy",
        return: "Select a return policy",
      },
    },
  },
  gated: {
    description: "Your free trial has ended, please update your billing details to continue using this product.",
    selectPlan: "Select Plan",
    title: "Update billing details",
  },
  import: {
    addValue: "Add a value and press Enter",
    allEbayCategories: "All categories",
    allProductsSelected_one: "{{count, number}} product selected",
    allProductsSelected_other: "All {{count, number}} products selected",
    backToListing: "Back to eBay listing",
    chooseCategory: "Choose a category",
    chooseCategoryForSpecifics: "Choose a category to see its item specifics.",
    chooseValue: "Choose a value",
    chooseValues: "Choose values",
    clearSpecific: "Clear",
    collectionFallbackHint: "Item specifics left empty here take the value or mapping the {{collection}} collection has for them.",
    collectionsFallbackHint: "Item specifics left empty here take the value or mapping the {{collections}} collections have for them.",
    doneMapping: "Done",
    ebayCategoriesFailed: "Couldn't load the eBay categories",
    ebayCategoriesFirstLoad: "Getting eBay's categories, the first time takes a few seconds",
    ebayCategory: "eBay category",
    ebayCategoryFor: "Choose where “{{title}}” is listed on eBay. Without saved details it's listed in its collection's category or the one eBay suggests, with item specifics filled in from the product.",
    ebayCategoryFromCollection: "From {{collection}}",
    ebayCategoryOfCollection: "The category of the {{collection}} collection",
    ebayCategorySuggested: "Suggested by eBay",
    ebayCategorySuggestionLoading: "Getting eBay's suggestion…",
    ebayListing: "eBay listing",
    editEbayCategory: "Change eBay category",
    editEbayCategoryFor: "Change the eBay category of {{title}}",
    enterValue: "Enter a value",
    fallbackFieldHint: "Left empty, it's filled from the product's “{{field}}” field, as the {{collection}} collection maps it.",
    fallbackMetafieldHint: "Left empty, it's filled from the product's {{metafield}} metafield, as the {{collection}} collection maps it.",
    fallbackValueHint: "Left empty, it takes “{{value}}” from the {{collection}} collection.",
    importEbayProducts: "Import eBay products",
    importShopifyProducts: "Import Shopify products",
    itemSpecifics: "Item specifics",
    itemSpecificsCount_one: "{{count}} item specific",
    itemSpecificsCount_other: "{{count}} item specifics",
    itemSpecificsDescription: "Optional details about “{{title}}” that eBay shows on the listing and uses to help buyers find it. Empty ones are filled in from the product where it names a value, and required ones say they don't apply otherwise.",
    itemSpecificsFailed: "Couldn't load the item specifics",
    mapMetafieldsDescription: "Choose the Shopify product vendor or metafield each item specific of {{category}} is filled from when “{{title}}” is imported. Its value comes before the one entered here.",
    mappedFieldHint: "Filled from the product's “{{field}}” field in Shopify. The value here is used when it's empty.",
    mappedSpecificHint: "Filled from the product's {{metafield}} metafield. The value here is used when it's empty.",
    migrateToEbay: "Select products to migrate to your eBay account",
    noEbayCategories: "No categories found",
    noEbayCategorySuggestion: "eBay has no suggestion for this product, choose a category",
    noItemSpecifics: "eBay has no item specifics for this category.",
    optionalSpecifics: "Optional",
    recommendedSpecifics: "Recommended",
    removeSavedListing: "Remove saved details",
    removeSavedListingHint: "The product is listed in its collection's category or the one eBay suggests, with item specifics filled in from the product",
    removeValue: "Remove {{value}}",
    requiredSpecific: "Required",
    requiredSpecifics: "Required",
    sameProductWarning: "Importing the same product will create a new Shopify product and link to the latest version",
    saveListingFailed: "Couldn't save the eBay details",
    searchEbayCategories: "Search categories",
    searchValues: "Search values",
    selectAllProducts_one: "Select {{count, number}} product",
    selectAllProducts_other: "Select all {{count, number}} products",
    selectDestination: "Select destination",
    selectEbayAccount: "Select an eBay account before importing products",
    selectShopifyAccount: "Select a Shopify account before importing products",
    selectedCount_one: "{{count}} selected",
    selectedCount_other: "{{count}} selected",
    setBusinessPolicies: "Set the business policies for eBay account \"{{alias}}\" before importing products",
    showOptionalSpecifics_one: "Show {{count}} optional item specific",
    showOptionalSpecifics_other: "Show {{count}} optional item specifics",
    specificDependsOn: "Set {{aspects}} first",
    undoSelectAllProducts: "Undo select all products",
    unselectAll: "Unselect all",
    useCollectionCategory: "Use the {{collection}} collection's category",
    useEbaySuggestion: "Use eBay's suggestion",
  },
  integrations: {
    account: {
      details: "Details",
      displayName: "This is your display name",
      expiresOn: "Integration expires on {{date}}",
      fetchDescription: "Fetch all your items and bring them under Inventoree's management.",
      fetchHint: "(You can update your products by clicking this again.)",
      fetchItems: "Fetch Items",
      fetchTitle: "Fetch",
      startImporting: "Start importing",
    },
    accountTitle: "Account -",
    actions: {
      fetch: "Fetch",
      import: "Import",
      reconnect: "Reconnect",
      toggleSync: "Toggle sync",
    },
    grid: {
      empty: "No integrations connected yet. Connect your first platform below.",
      expires: "Expires: {{date}}",
    },
    messaging: {
      title: "Buyer Messaging",
    },
    nav: {
      account: "Account",
      import: "Import",
      messaging: "Messaging",
      reviews: "Reviews",
    },
    remove: {
      button: "Remove Account",
      confirm: "Remove",
      dataLoss: "Please note that all associated data will be removed and cannot be recovered.",
      description: "Are you sure you want to remove this account? All associated data will be removed and this cannot be undone.",
      removing: "Removing...",
      shopifyAdminOnly: "Shopify accounts can only be removed from the Shopify admin dashboard.",
      shopifyBillingAccount: "If this is the billing account, the account will be scheduled for deletion.",
      title: "Remove {{alias}}",
    },
    status: {
      connected: "Connected",
      disconnected: "Disconnected",
      scopeUpdateRequired: "Scope Update Required",
      syncingOff: "Syncing Off",
      syncingOn: "Syncing On",
    },
  },
  inventory: {
    allSelected_one: "{{count, number}} product selected",
    allSelected_other: "All {{count, number}} products selected",
    delete: {
      confirmMany: "Are you sure you want to delete the products? This action will:",
      confirmOne: "Are you sure you want to delete the product? This action will:",
      linksMany: "Remove platform links for the products",
      linksOne: "Remove platform links for the product",
      removeMany: "Permanently remove the products from your inventory",
      removeOne: "Permanently remove the product from your inventory",
      titleMany: "Delete {{count, number}} Products",
      titleOne: "Delete Product",
      variants: "Delete all associated variants and inventory records",
    },
    empty: {
      cta: "Go to Dashboard to fetch your Products",
      description: "Get started by fetching products from your connected stores",
      title: "No Products Yet",
    },
    missingSkus: "Missing SKUs",
    noSku: "No SKU",
    notSynced: "Not synced",
    selectAll_one: "Select {{count, number}} product",
    selectAll_other: "Select all {{count, number}} products",
    sku: {
      confirm: "You are about to generate SKUs for {{count, number}} products. This action will:",
      irreversible: "This action cannot be undone",
      platforms: "Update SKUs in all linked platforms",
      replace: "Replace existing SKUs if they already exist",
      settings: "Generate new SKUs based on your current SKU settings",
      title: "Generate SKUs for {{count, number}} Products",
    },
    synced: "Synced",
    toast: {
      deleting: "Deleting products",
      generatingSkus: "Generating SKUs",
      updating: "Updating",
    },
    undoSelectAll: "Undo select all products",
    variantCount_one: "{{count}} variant",
    variantCount_other: "{{count}} variants",
  },
  language: {
    label: "Language",
    select: "Select language",
  },
  messaging: {
    body: "Message Body",
    columns: {
      buyer: "Buyer",
      instant: "Instant",
      item: "Item",
      order: "Order",
      satisfaction: "Satisfaction",
      tracking: "Tracking",
    },
    instant: {
      description: "Triggered from the sale event and staged for your preview before sending.",
      placeholder: "Write the message buyers should receive after the order is placed.",
      title: "Instant Reassurance",
    },
    orders: {
      autoSendDescription: "When enabled, READY instant, tracking, and satisfaction messages are sent automatically.",
      autoSendOn: "Auto Send On",
      count_one: "{{count}} order",
      count_other: "{{count}} orders",
      description: "Review each order step, preview the rendered template, and manually send when ready. Showing orders from the last 30 days.",
      disableAutoSend: "Disable Auto Send",
      empty: "No eBay orders have entered the messaging queue yet.",
      enableAutoSend: "Enable Auto Send",
      manualSend: "Manual Send",
      preview: "Preview",
      title: "Order Messages",
    },
    placeholdersHint: "Available placeholders are inserted when the message preview is rendered.",
    preview: {
      description: "Review the rendered message before sending it to the buyer.",
      loading: "Loading preview...",
      renderedBody: "Rendered Body",
      send: "Send Message",
      subject: "Subject",
      title: "Preview Message",
    },
    queue: {
      description: "When enabled, eBay events prepare message drafts for review so you can preview and manually send each step.",
      disable: "Disable Queue",
      enable: "Enable Queue",
      title: "Messaging Queue",
    },
    satisfaction: {
      description: "Marked ready once the follow-up becomes due, then available for preview and manual send.",
      placeholder: "Write the message buyers should receive after delivery.",
      title: "Satisfaction Check",
    },
    save: "Save Messaging Settings",
    status: {
      failed: "Failed",
      pending: "Pending",
      ready: "Ready",
      sent: "Sent",
      skipped: "Skipped",
    },
    tracking: {
      description: "Triggered when shipment tracking is available and staged for manual review.",
      placeholder: "Write the message buyers should receive when tracking is uploaded.",
      title: "Tracking Push",
    },
    useTemplate: "Use Template",
  },
  nav: {
    activity: "Activity",
    collections: "Collections",
    dashboard: "Dashboard",
    documentation: "Documentation",
    homepage: "Homepage",
    inventory: "Inventory",
    settings: "Settings",
    signOut: "Sign out",
    toggleTheme: "Toggle theme",
  },
  notifications: {
    clearAll: "Clear all",
    delete: "Delete notification",
    empty: "No notifications",
    learnMore: "Learn more",
    markAllRead: "Mark all read",
    title: "Notifications",
  },
  onboard: {
    chooseCurrency: "Choose currency",
    namePlaceholder: "Your organization name",
    slugPreview: "Here's what your slug may look like",
    title: "We just need a little more information",
    validation: {
      currency: "supported currencies are USD, GBP, EUR",
      nameChars: "name can only contain letters, numbers, and spaces",
      nameMax: "name must be at most 100 characters long",
      nameMin: "name must be at least 3 characters long",
    },
  },
  orderSync: {
    connectShopifyFirst: "Connect a Shopify account before enabling order sync.",
    ebay: {
      chooseAccount: "Choose the Shopify account to store all new eBay orders",
      description: "Sync new eBay orders to Shopify",
    },
    selectShopifyAccount: "Select Shopify account",
    tiktok: {
      chooseAccount: "Choose the Shopify account to store all new TikTok Shop orders",
      description: "Sync new TikTok Shop orders to Shopify",
    },
    title: "Order sync",
  },
  platforms: {
    logoAlt: "{{platform}} logo",
  },
  product: {
    columns: {
      cost: "Cost",
      linkedAccounts: "Linked accounts",
      price: "Price",
      qty: "Qty",
      title: "Title",
    },
    cost: {
      invalid: "Price must be a valid amount (max 2 decimal places)",
      negative: "Price must be greater than or equal to 0",
    },
    deleting: "Deleting product",
    events: {
      alias: "Alias",
      createdAt: "Created At",
      description: "Quantity updates from synced platforms",
      eventType: "Event Type",
      loading: "Loading inventory events...",
      pageSummary: "Page {{page}} of {{total}} ({{totalCount}} total)",
      previousQty: "Previous Qty",
      quantity: "Quantity",
      title: "Inventory Sync Events",
    },
    sku: "SKU",
    title: "Title",
    variantsDescription: "A list of variants and the platforms they are on",
    variantsTitle: "Platform Product Variants",
  },
  reviews: {
    import: {
      button: "Import reviews",
      buttonAgain: "Import new reviews",
      description: "Import the feedback buyers left you on eBay as reviews for your store. Positive, neutral and negative feedback become 5, 3 and 1 star reviews, the filters below choose which are published.",
      errors: {
        expired: "Reconnect this eBay account on its Account page, then import its reviews again.",
        failed: "The reviews couldn't be imported. Please try again.",
        stopped: "The import stopped before it finished. Please try again.",
      },
      imported: "Imported",
      importing: "Importing...",
      lastImport: "Last import",
      never: "Never",
      published: "Published",
      running: "Importing your eBay reviews. This can take a few minutes, you can leave this page.",
      title: "eBay reviews",
    },
    list: {
      columns: {
        action: "Action",
        buyer: "Buyer",
        date: "Date",
        item: "Item",
        rating: "Rating",
        review: "Review",
        status: "Status",
      },
      empty: "No reviews yet. Import your eBay reviews to see them here.",
      emptyFiltered: "No reviews match these filters.",
      filters: {
        all: "All reviews",
        anyRating: "Any rating",
        notPublished: "Not published",
        published: "Published",
        stars_one: "{{count}} star",
        stars_other: "{{count}} stars",
      },
      hide: "Hide",
      nextPage: "Next page",
      page: "Page {{page}} of {{totalPages}}",
      previousPage: "Previous page",
      publish: "Publish",
      ratingLabel: "{{rating}} out of 5 stars",
      reply: "Your reply: {{reply}}",
      status: {
        filtered: "Filtered out",
        hidden: "Hidden by you",
        published: "Published",
        shown: "Shown by you",
      },
      storeOnly: "Store reviews only",
      title: "Reviews",
      total_one: "{{count}} review",
      total_other: "{{count}} reviews",
      useFilters: "Use filters",
    },
    loadFailed: "Failed to load the reviews of this account.",
    plan: {
      description: "Upgrade your plan to import your eBay feedback as reviews, show them on your store and export them for Judge.me.",
      title: "Available on the Starter plan or above",
      viewPlans: "View plans",
    },
    settings: {
      excludeKeywords: "Hide reviews that mention",
      excludeKeywordsHelp: "Words or phrases, separated by commas",
      excludeKeywordsPlaceholder: "e.g. broken, late, refund",
      manualNote: "Reviews you publish or hide yourself in the list below stay that way whatever the filters say.",
      minLength: "Shortest comment",
      minLengthHelp: "In characters, 0 publishes every length",
      noStore: "No store, only download them",
      ratingNegative: "Negative (1 star)",
      ratingNeutral: "Neutral (3 stars)",
      ratingPositive: "Positive (5 stars)",
      ratings: "Ratings to publish",
      save: "Save settings",
      skipAutomatic: "Skip eBay's automatic feedback",
      skipAutomaticHelp: "eBay leaves \"Order delivered on time with no issues\" for buyers who don't leave feedback themselves",
      store: "Show reviews on",
      storeHelp: "Your reviews are shown on this store with the Inventoree review blocks",
      title: "Which reviews are published",
    },
    storefront: {
      blocks: {
        carousel: {
          action: "Add to home page",
          description: "Your store's newest reviews as a carousel or grid, on any page.",
          title: "Reviews carousel",
        },
        floating: {
          action: "Turn on",
          description: "Your store's rating in a corner of every page, it opens your newest reviews.",
          title: "Floating reviews badge",
        },
        product: {
          action: "Add to product page",
          description: "Each product's reviews with its rating breakdown, on product pages.",
          title: "Product reviews",
        },
        stars: {
          action: "Add to product page",
          description: "The product's stars and number of reviews, e.g. under its title.",
          title: "Star rating",
        },
      },
      chooseStore: "Choose a store under \"Show reviews on\" to add your reviews to its theme.",
      description: "Add Inventoree's review blocks to your theme in the theme editor. They show the reviews you publish and update after each import or change.",
      judgeme: {
        button: "Download CSV",
        description: "Download your published reviews in Judge.me's import format. In Judge.me, go to Settings, Import reviews, Import from apps, choose \"Judge.me format\" and upload the file. Reviews keep their eBay dates and your replies, and Judge.me doesn't email your buyers.",
        title: "Using Judge.me?",
      },
      title: "Show reviews on your store",
    },
    toast: {
      downloadFailed: "Failed to download the reviews",
      downloaded_one: "Downloaded {{count}} review",
      downloaded_other: "Downloaded {{count}} reviews",
      importFailed: "Failed to start the import",
      importRunning: "Reviews are already being imported",
      importStarted: "Importing your eBay reviews",
      nothingToDownload: "There aren't any published reviews to download yet",
      reviewFailed: "Failed to change the review",
      reviewFiltered: "Review follows your filters again",
      reviewHidden: "Review hidden",
      reviewShown: "Review published",
      settingsFailed: "Failed to save the review settings",
      settingsSaved: "Review settings saved",
      settingsSavedStore: "Review settings saved, your store is being updated",
    },
  },
  settings: {
    billing: {
      changePlanHint: "You can change your plan if you see consistent orders.",
      choosePlan: "Choose plan",
      chooseShopifyAccount: "Choose shopify account",
      currentPlan: "Current plan:",
      installShopifyApp: "Please install the shopify app to connect your account and choose your subscription, here's a link to our app:",
      manageBilling: "Manage billing",
      orders: "Orders",
      plan: "Plan",
      trialEnds: "Your free trial ends on <bold>{{date}}</bold>",
      updateSubscription: "Update subscription",
      usage: "Usage",
      usageCost: "Usage Cost",
    },
    nav: {
      billing: "Billing",
      organization: "Organization",
      skuGenerator: "SKU Generator",
    },
    organization: {
      connectStores: {
        back: "Back",
        choose: "Which store is this?",
        codeExpired: "This code has expired.",
        codeExpiresIn: "Expires in {{time}}",
        codeFailed: "Failed to generate a code. Please try again.",
        codeHint: "Enter this code on the store you want to connect. It can only be used once.",
        connect: "Connect store",
        connected: "Store connected",
        connectedBody: "This store is now part of {{name}}. Its products are being imported and linked to {{name}}'s products by SKU.",
        connectedLogin: "Log in with your main store's account to manage both stores.",
        copied: "Code copied",
        copy: "Copy code",
        description: "Connect your stores to one organization so they share inventory, settings and one plan.",
        errors: {
          failed: "Failed to connect the store. Please try again.",
          invalidCode: "This code is invalid or has expired. Generate a new code on your main store.",
          organizationDeleted: "The main store's organization is no longer active.",
          sameOrganization: "This code was generated by this store. Enter it on the store you want to connect.",
        },
        generate: "Generate new code",
        generating: "Generating code...",
        input: "Code from your main store",
        inputPlaceholder: "XXXX-XXXX-XXXX",
        install: "Install Inventoree from the Shopify App Store",
        installHint: "The other store needs Inventoree installed first.",
        logout: "Log out",
        main: "Main store",
        mainDescription: "Generate a code to enter on your other stores. This store's plan covers them.",
        open: "Open {{name}}",
        paidPlan: "This store is on the {{plan}} plan. You must cancel the plan before connecting this store, the main store's plan covers it once it's connected.",
        paidPlanTitle: "Cancel your plan first",
        secondary: "Secondary store",
        secondaryDescription: "Enter the code from your main store to move this store to its organization.",
        secondaryWarning: "This store's integrations, orders and settings move to the main store's organization and this organization is deleted. Its products are imported again and linked to the main store's products by SKU.",
        title: "Connect stores",
        viewBilling: "Go to billing",
      },
      currencies: {
        EUR: "EUR - Euro",
        GBP: "GBP - British Pound",
        USD: "USD - US Dollar",
      },
      currency: "Currency",
      currencyPlaceholder: "Select a currency",
      currentCurrency: "Current Currency:",
      currentName: "Organization Name:",
      currentTitle: "Current Settings",
      loadFailed: "Unable to load user information. Please try refreshing the page.",
      name: "Organization Name",
      namePlaceholder: "Enter organization name",
      slug: "Organization Slug",
      submit: "Update Settings",
      title: "Organization Settings",
      updateFailed: "Failed to update organization settings. Please try again.",
      updated: "Organization settings updated successfully!",
      validation: {
        currency: "supported currencies are USD, GBP, EUR",
        nameMin: "name must be at least 3 characters long",
      },
    },
  },
  sidebar: {
    mobileDescription: "Displays the mobile sidebar.",
    title: "Sidebar",
    toggle: "Toggle Sidebar",
  },
  skuGenerator: {
    advanced: "Advanced Settings",
    advancedDescription: "Configure character sets, SKU length, and retry behavior.",
    affixHint: "Max 4 characters, alphanumeric only",
    autoCreate: "Generate SKUs automatically for new products with empty SKUs",
    basicFormat: "Basic Format Settings",
    characterSet: "Character Set",
    characterSets: {
      alphabetic: "Alphabetic (A-Z)",
      alphanumeric: "Alphanumeric (A-Z, 0-9)",
      custom: "Custom",
      numeric: "Numeric (0-9)",
    },
    customCharacters: "Custom Characters",
    customCharactersHint: "Enter the characters to use for SKU generation",
    customCharactersPlaceholder: "e.g., ABCDEF123456",
    delimiter: "Delimiter",
    delimiterHint: "Character between parts",
    delimiters: {
      dot: "Dot (.)",
      hyphen: "Hyphen (-)",
      none: "None",
      underscore: "Underscore (_)",
    },
    generalDescription: "Configure basic SKU generation settings and auto-generation behavior.",
    generalTitle: "General Settings",
    generateSkus: "Generate SKUs",
    length: "SKU Length",
    lengthHint: "Length of generated SKU (1-50 characters)",
    maxRetries: "Max Retries",
    maxRetriesHint: "Maximum retry attempts for conflicts (1-10)",
    optional: "optional",
    prefix: "Prefix",
    save: "Save Settings",
    strategies: {
      product: {
        description: "Use product title and options",
        label: "Product",
      },
      random: {
        description: "Generate random alphanumeric SKUs",
        label: "Random",
      },
      template: {
        description: "Use custom templates with placeholders",
        label: "Template",
      },
    },
    strategy: "Generation Strategy",
    strategyPlaceholder: "Select generation strategy",
    suffix: "Suffix",
    template: "SKU Template",
    templateAvailable: "Available: {{placeholders}}",
    templateConfig: "Template Configuration",
    templateExample: "Example: \"STORE-{TITLE:3}-{COUNTER:4}\" → \"STORE-WIR-0001\" (for \"Wireless Headphones\")",
    templatePlaceholder: "e.g., {PREFIX}-{TITLE:3}-{COUNTER:4}",
    validation: {
      alphanumeric: "Only alphanumeric characters allowed",
      maxLength: "Must be 4 characters or fewer",
    },
  },
  syncConfig: {
    archiveProduct: {
      description: "Archive this account's Shopify product when a synced quantity update brings the product's total quantity to 0. It stays archived until you unarchive it in Shopify.",
      label: "Archive Shopify product at 0 quantity",
    },
    autoExport: {
      description: "Automatically export products from this account to your selected {{platform}} accounts.",
      label: "Automated exports",
      targetsLabel: "Export to {{platform}} accounts",
      targetsPlaceholder: "Select {{platform}} accounts",
      targetsRequired: "Select at least one {{platform}} account.",
    },
    endListing: {
      description: "End this account's eBay listing when synced Shopify quantity brings the product's total quantity to 0.",
      label: "End eBay listing at 0 quantity",
    },
    externalOrderImport: {
      description: "Turn on if an app such as Marketplace Connect creates your eBay orders in Shopify. Inventoree then takes an eBay sale off your stock when its order reaches Shopify, so the sale isn't deducted twice. Quantity changes you make on eBay aren't copied to your other accounts. Leave this off if Inventoree's order sync creates your eBay orders.",
      label: "eBay orders are imported into Shopify by another app",
    },
    fields: {
      description: "Description",
      price: "Price",
      quantity: "Quantity",
      title: "Title",
    },
    fieldsDescription: "Choose which fields sync when a product is updated in this account:",
    title: "Sync settings",
  },
  table: {
    collections: "Collections",
    collectionsHelp: "Products in any of the checked collections",
    columns: {
      activity: "Activity",
      availableQty: "Available Qty",
      inventory: "Inventory",
      note: "Note",
      platforms: "Platforms",
      product: "Product",
      sku: "SKU",
      status: "Status",
      synced: "Synced",
    },
    columnsTitle: "Columns",
    filters: "Filters",
    firstPage: "Go to first page",
    forVariants_one: "for {{count}} variant",
    forVariants_other: "for {{count}} variants",
    inStock_one: "{{count}} in stock",
    inStock_other: "{{count}} in stock",
    lastPage: "Go to last page",
    linkedStores: "Linked Stores",
    next: "Next",
    nextPage: "Go to next page",
    noResultsFetch: "No results, start by fetching your products",
    pageOf: "Page {{page}} of {{total}}",
    previous: "Previous",
    previousPage: "Go to previous page",
    rowsPerPage: "Rows per page",
    rowsSelected: "{{selected}} of {{total}} row(s) selected.",
    searchProduct: "Search for product...",
    selectAll: "Select all",
    selectRow: "Select row",
    showingProducts: "Showing {{from}} to {{to}} of {{total}} products",
    sort: {
      quantityAsc: "Quantity ascending",
      quantityDesc: "Quantity descending",
      skuAsc: "SKU ascending",
      skuDesc: "SKU descending",
      titleAsc: "Title ascending",
      titleDesc: "Title descending",
    },
    sortBy: "Sort by",
    stores: "Stores",
    syncFilter: {
      all: "All",
      synced: "Synced",
      unsynced: "Unsynced",
    },
    syncStatus: "Sync Status",
  },
  toast: {
    activityDeleted: "Deleted activity",
    aliasFailed: "Failed to update alias",
    aliasUpdated: "Updated alias",
    autoSendDisabled: "Auto-send disabled",
    autoSendEnabled: "Auto-send enabled",
    autoSendFailed: "Failed to update auto-send setting",
    automationDisabled: "Messaging automation disabled",
    automationEnabled: "Messaging automation enabled",
    automationFailed: "Failed to update messaging automation",
    costUpdated: "Updated cost price",
    deleteProductsFailed: "Failed to delete products",
    deletedProducts_one: "Deleted {{count, number}} product",
    deletedProducts_other: "Deleted {{count, number}} products",
    ebayConnectFailed: "Failed to connect eBay account: {{error}}",
    ebayConnectFailedGeneric: "Failed to connect eBay account",
    ebayConnected: "eBay account connected!",
    ebayFetchStarted: "eBay product fetch started. Check Activity Logs for progress.",
    fetchEbayFailed: "Failed to fetch eBay products",
    fetchRunning: "{{platform}} products are already being fetched. Check Activity Logs for progress.",
    fetchShopifyFailed: "Failed to fetch Shopify products",
    fetchTiktokFailed: "Failed to fetch TikTok products",
    fetchingShopify: "Fetching Shopify products",
    fetchingTiktok: "Fetching TikTok products",
    importAllLimit: "Importing everything in an account is limited to 5,000 products at a time and this account has more. Import them in batches of up to 1,000 by selecting the products to import.",
    importFailed: "Failed to import products",
    importNothingNew: "Every product in this store is already on that eBay account",
    importStarted: "Importing has begun",
    importWillBegin: "Importing will begin",
    messageNotReady: "Message cannot be sent yet",
    messageSent: "Message sent",
    messagingFailed: "Failed to update messaging settings",
    messagingUpdated: "Messaging settings updated",
    migrationFailed: "Migration failed: {{error}}",
    migrationStarted: "Migration started! Job ID: {{jobId}}",
    orderSyncDisabled: "Order sync disabled",
    orderSyncEnabled: "Order sync enabled",
    orderSyncFailed: "Failed to update order sync",
    policiesFailed: "Failed to update eBay policies",
    policiesUpdated: "Business policies updated",
    previewFailed: "Failed to preview message",
    productSyncFailed: "Failed to update product sync state",
    productSyncUpdated: "Updated product sync state",
    removeEbayFailed: "Failed to remove eBay account",
    sendFailed: "Failed to send message",
    shopifyReconnectFailed: "Failed to reconnect Shopify account",
    shopifyScopesUpToDate: "Shopify scopes are up to date — no reconnect needed.",
    skuSettingsFailed: "Failed to update SKU settings",
    skuSettingsUpdated: "SKU settings have been updated",
    skusFailed: "Failed to generate new SKUs",
    skusGenerated: "Generated new SKUs",
    subscriptionFailed: "Failed to create subscription",
    syncSettingsFailed: "Failed to update sync settings",
    syncSettingsUpdated: "Sync settings updated",
    syncStateFailed: "Sync state update failed",
    syncStateUpdated: "Sync state updated",
    upgradeForCredits: "For unlimited credits upgrade to Essential",
  },
};

type Strings<T> = {
  [K in keyof T]: T[K] extends string ? string : Strings<T[K]>;
} & {
  // plural "many" form, used by languages such as French and Spanish
  [K in keyof T as K extends `${infer Base}_other` ? `${Base}_many` : never]?: string;
};

export type Translation = Strings<typeof en>;
